import { forwardRef, useMemo } from 'react';
import { Link as RouterLink, useSearchParams, type LinkProps } from 'react-router-dom';

import { useAuthContext } from 'src/auth/hooks';
import { appendPlatformId } from 'src/utils/append-platform-id';

/**
 * Custom Link component that ensures platform_id is always preserved in search params.
 * Works exactly like the native Link from react-router-dom.
 */
export const Link = forwardRef<HTMLAnchorElement, LinkProps>(({ to, ...props }, ref) => {
  const [searchParams] = useSearchParams();
  const { platformId } = useAuthContext();

  const modifiedTo = useMemo(() => {
    // Get platform_id from context or current URL
    const currentPlatformId = platformId || searchParams.get('platform_id');
    return appendPlatformId(to, currentPlatformId);
  }, [to, platformId, searchParams]);

  return <RouterLink ref={ref} to={modifiedTo} {...props} />;
});

Link.displayName = 'Link';
